import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Lock, LogIn } from 'lucide-react';
import { BracketTournamentPanel } from './bracket/BracketTournamentPanel';

const FORMAT_OPTIONS = [
  { value: 'round_robin', title: 'Round Robin', desc: 'Every team plays every other team once', color: 'bg-green-500', free: true },
  { value: 'double_round_robin', title: 'Double Round Robin', desc: 'Every team plays every other team twice (home & away)', color: 'bg-teal-500', free: false },
  { value: 'single_elimination', title: 'Single Elimination', desc: "Knockout bracket - lose once and you're out", color: 'bg-blue-500', free: false },
  { value: 'double_elimination', title: 'Double Elimination', desc: 'Lose twice before elimination', color: 'bg-purple-500', free: false },
  { value: 'hybrid', title: 'Hybrid (Round Robin + Playoffs)', desc: 'Group stage followed by elimination playoffs', color: 'bg-amber-500', free: false },
  { value: 'bracket', title: 'Bracket Tournament', desc: 'Pick a Bracket Library format that fits your teams (single, double or 3-game guarantee), set seeds and byes, and preview it', color: 'bg-rose-500', free: false },
];

const BRACKET_FORMATS = ['single_elimination', 'double_elimination', 'consolation'];

export function FormatStep({ eventData, setEventData, isAuthenticated, showAuthPrompt, setShowAuthPrompt }) {
  const isBracket = eventData.bracket_tournament || eventData.format === 'consolation';
  const selected = isBracket ? 'bracket' : eventData.format;
  const choose = (value) => setEventData((prev) => (value === 'bracket'
    ? { ...prev, bracket_tournament: true, format: BRACKET_FORMATS.includes(prev.format) ? prev.format : (prev.teams.length === 8 ? 'consolation' : 'single_elimination'), bracket_positions: null }
    : { ...prev, bracket_tournament: false, format: value, bracket_positions: null }));
  return (
    <div className="space-y-6 wizard-step">
      {!isAuthenticated && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
          <Lock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800">Account Required for Advanced Formats</p>
            <p className="text-xs text-amber-600 mt-1">Round Robin is free for everyone. Other formats require a free account.</p>
          </div>
        </div>
      )}

      <RadioGroup value={selected}
        onValueChange={choose}
        className="grid gap-4">
        {FORMAT_OPTIONS.map((option, i) => (
          <div key={option.value} className="animate-fade-in-up" style={{ animationDelay: `${i * 0.1}s` }}>
            <Label htmlFor={option.value} data-testid={`format-${option.value}`}
              className={`flex items-start gap-4 p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                selected === option.value ? 'border-[#00247D] bg-[#00247D]/5' : 'border-slate-200 hover:border-slate-300'
              }`}>
              <RadioGroupItem value={option.value} id={option.value} className="mt-1" />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${option.color}`}></div>
                  <span className="font-bold text-slate-800" style={{ fontFamily: 'Barlow Condensed' }}>{option.title}</span>
                  {option.free ? (
                    <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded">FREE</span>
                  ) : !isAuthenticated ? (
                    <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs font-medium rounded flex items-center gap-1">
                      <Lock className="w-3 h-3" /> Account Required
                    </span>
                  ) : null}
                </div>
                <p className="text-sm text-slate-500 mt-1">{option.desc}</p>
              </div>
            </Label>
          </div>
        ))}
      </RadioGroup>
      {isBracket && <BracketTournamentPanel eventData={eventData} setEventData={setEventData} />}

      {showAuthPrompt && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-md w-full animate-fade-in-up">
            <CardHeader className="text-center">
              <div className="w-16 h-16 bg-[#00247D]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <LogIn className="w-8 h-8 text-[#00247D]" />
              </div>
              <CardTitle className="text-xl">Account Required</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-slate-600 text-center">
                {eventData.format !== 'round_robin'
                  ? `The ${eventData.format.replace('_', ' ')} format requires a free account.`
                  : 'This feature requires a free account.'}
              </p>
              <p className="text-sm text-slate-500 text-center">Create an account to unlock all tournament formats, pool play, and more features!</p>
              <div className="flex gap-3">
                <Button variant="outline" className="flex-1" onClick={() => setShowAuthPrompt(false)}>Cancel</Button>
                <Link to="/login" className="flex-1">
                  <Button className="w-full bg-[#00247D] hover:bg-[#001A5C]"><LogIn className="w-4 h-4 mr-2" />Sign In / Sign Up</Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
